import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import ImageCompressor from '../components/ImageCompressor';

const { 
  FiZap, FiShield, FiDownload, FiSettings, FiImage, 
  FiStar, FiUsers, FiTrendingUp, FiClock, FiCheckCircle, FiLayers 
} = FiIcons;

function ImageCompress() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: FiZap,
      title: "Smart Compression",
      description: "Advanced algorithms reduce file size while preserving image quality and visual clarity"
    },
    {
      icon: FiShield,
      title: "100% Secure",
      description: "All compression happens locally in your browser - your images never leave your device"
    },
    {
      icon: FiSettings,
      title: "Adjustable Quality",
      description: "Fine-tune compression levels from 10% to 100% to find the perfect balance"
    },
    {
      icon: FiDownload,
      title: "Instant Download",
      description: "Download your compressed images immediately with no waiting or processing delays"
    },
    {
      icon: FiImage,
      title: "Multiple Formats",
      description: "Support for JPEG, PNG, and WebP with format conversion capabilities"
    },
    {
      icon: FiStar,
      title: "Free Forever",
      description: "No registration, no limits, no watermarks. Completely free image compression"
    }
  ];

  const benefits = [
    {
      icon: FiClock,
      title: "Faster Websites",
      description: "Reduce page load times by compressing images for web optimization"
    },
    {
      icon: FiTrendingUp,
      title: "Save Storage",
      description: "Free up disk space by reducing image file sizes without quality loss"
    },
    {
      icon: FiCheckCircle,
      title: "SEO Boost",
      description: "Improve website performance and search engine rankings with optimized images"
    }
  ];

  const useCases = [
    {
      title: "Website Optimization",
      description: "Compress images for faster website loading and better user experience",
      icon: "🌐"
    },
    {
      title: "Email Attachments",
      description: "Reduce image sizes to meet email attachment size limits",
      icon: "📧"
    },
    {
      title: "Social Media",
      description: "Optimize images for social media platforms while maintaining quality",
      icon: "📱"
    },
    {
      title: "Cloud Storage",
      description: "Save cloud storage space by compressing photos and images",
      icon: "☁️"
    },
    {
      title: "Mobile Apps",
      description: "Reduce app size and improve performance with compressed images",
      icon: "📲"
    },
    {
      title: "Print Preparation",
      description: "Balance file size and print quality for professional printing",
      icon: "🖨️"
    }
  ];

  return (
    <>
      <SEOHead
        title="Free Image Compressor Online - Reduce Image File Size Instantly"
        description="Compress images online for free! Reduce JPEG, PNG, WebP file sizes by up to 90% while maintaining quality. No registration required, secure browser-based compression."
        keywords="image compressor, compress images online, reduce image size, image optimization, photo compressor, free image compression, JPEG compressor, PNG compressor, WebP compression, image file size reducer"
        url="/compress"
        image="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=630&fit=crop"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-green-50 to-emerald-100">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <div className="bg-green-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiZap} className="text-white text-3xl" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                Free Image Compressor
                <span className="block text-green-600">Reduce File Sizes</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Compress your images instantly while maintaining quality. Reduce file sizes by up to 90% 
                with our advanced compression algorithms - completely free and secure.
              </p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl mx-auto">
                {[
                  { number: "90%", label: "Size Reduction" },
                  { number: "50MB", label: "Max File Size" },
                  { number: "3+", label: "Output Formats" },
                  { number: "0$", label: "Cost Forever" }
                ].map((stat, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="text-3xl font-bold text-green-600">{stat.number}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Image Compressor Tool */}
        <section className="py-16 px-4 sm:px-6 lg:px-8">
          <ImageCompressor />
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Advanced Image Compression Features
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Professional-grade compression technology for optimal results
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-gray-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300"
                >
                  <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <SafeIcon icon={feature.icon} className="text-green-600 text-xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Batch Compress Promotion */}
        <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="bg-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiLayers} className="text-white text-3xl" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Need to Compress Multiple Images?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Try our powerful batch image compressor! Process hundreds of images at once with the same 
                professional compression quality and security.
              </p>
              <Link to="/batch-compress">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors flex items-center mx-auto"
                >
                  <SafeIcon icon={FiLayers} className="mr-2" />
                  Try Batch Compression Free
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Compress Your Images?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover the benefits of optimized images for your projects
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="text-center bg-gray-50 rounded-2xl p-8"
                >
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={benefit.icon} className="text-green-600 text-2xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-600">
                    {benefit.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Use Cases Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Perfect for Every Use Case
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From web optimization to storage management, our compressor handles it all
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {useCases.map((useCase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 hover:shadow-lg transition-all duration-300"
                >
                  <div className="text-4xl mb-4">{useCase.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {useCase.title}
                  </h3>
                  <p className="text-gray-600">
                    {useCase.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                How Image Compression Works
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Compress your images in just three simple steps
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: "1",
                  title: "Upload Your Image",
                  description: "Drag and drop your image or click to select from your device. We support all major image formats."
                },
                {
                  step: "2",
                  title: "Adjust Settings",
                  description: "Choose your desired quality level and output format. Preview the compression results in real-time."
                },
                {
                  step: "3",
                  title: "Download Compressed",
                  description: "Download your optimized image with reduced file size while maintaining visual quality."
                }
              ].map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="text-center"
                >
                  <div className="bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">
                    {step.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Image Compression FAQ
              </h2>
            </motion.div>

            <div className="space-y-8">
              {[
                {
                  question: "How much can I compress my images?",
                  answer: "You can typically reduce image file sizes by 50-90% depending on the original format and quality settings. JPEG images usually compress more than PNG files."
                },
                {
                  question: "Will compression affect image quality?",
                  answer: "Our smart compression algorithms minimize quality loss. At 80% quality, most images look identical to the original while being significantly smaller."
                },
                {
                  question: "What image formats are supported?",
                  answer: "We support JPEG, PNG, WebP, and GIF formats. You can also convert between formats during compression for additional optimization."
                },
                {
                  question: "Is there a file size limit?",
                  answer: "You can compress images up to 50MB in size. For larger files, consider using our batch compressor or resizing the image first."
                },
                {
                  question: "Are my images stored on your servers?",
                  answer: "No! All compression happens locally in your browser. Your images never leave your device, ensuring complete privacy and security."
                },
                {
                  question: "Can I compress images for web use?",
                  answer: "Absolutely! Our compressor is perfect for web optimization. Use 70-85% quality for web images to balance file size and visual quality."
                }
              ].map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg p-6 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-gray-600">
                    {faq.answer}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-green-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-4">
                Ready to Compress Your Images?
              </h2>
              <p className="text-xl text-green-100 mb-8">
                Start optimizing your images now - it's fast, free, and secure!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-white text-green-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors"
                >
                  Start Compressing
                </motion.button>
                <Link to="/batch-compress">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-green-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-800 transition-colors"
                  >
                    Try Batch Compression
                  </motion.button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
}

export default ImageCompress;